import { IHeaderGroupAngularComp } from 'ag-grid-angular';
import { IHeaderParams, SortDirection } from 'ag-grid-community';
import { take } from 'rxjs';

import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, HostListener, computed, inject, signal } from '@angular/core';

import { ResourcesService } from '@mitsubishi/core';
import { StudyPerformanceFiltersStore } from '@mitsubishi/project/data-access';
import { OutputFilterDialogComponent, OutputFilterDialogData } from '@mitsubishi/shared/components';
import { OutputFilter, PerformanceFilter } from '@mitsubishi/shared/data-access';
import { IconComponent } from '@mitsubishi/shared/ui';
import { DialogService } from '@mitsubishi/ui/dialog';

type StudyPerformanceHeaderComponentParams = {
  subcategoryId?: string;
  fieldId?: string;
  updatePerformanceHeaderFilter?: () => void;
};

@Component({
  selector: 'apm-study-performance-header',
  templateUrl: './study-performance-header.component.html',
  styleUrls: ['./study-performance-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, NgClass],
})
export class StudyPerformanceHeaderComponent implements IHeaderGroupAngularComp {
  @HostBinding('class') class = 'd-block w-100';

  public params!: IHeaderParams & StudyPerformanceHeaderComponentParams;

  readonly displayName = signal<string | undefined>(undefined);
  readonly subCategoryId = signal<string | undefined>(undefined);
  readonly dataType = signal<number | undefined>(undefined);
  readonly fieldId = signal<string | undefined>(undefined);
  readonly headerHovered = signal<boolean>(false);

  readonly currentSorting = signal<SortDirection | undefined>(undefined);
  readonly hasDefaultFilter = signal<boolean>(false);

  readonly areFiltersEnabled = computed(() => !!this.subCategoryId() && this.dataType() === 1);
  readonly areDefaultFiltersEnabled = computed(() => this.dataType() !== 1);

  #filtersStore = inject(StudyPerformanceFiltersStore);
  #dialogService = inject(DialogService);
  #resourcesService = inject(ResourcesService);

  updatePerformanceHeaderFilter: (() => void) | undefined;

  readonly currentFilters = computed(() => {
    const subId = this.subCategoryId();

    if (!subId) {
      return null;
    }

    return this.#filtersStore.getSubcategoryPerformanceFilters(subId)();
  });

  readonly filtered = computed(() => {
    const fieldId = this.fieldId();

    if (!fieldId) {
      return false;
    }

    return !!this.currentFilters()?.[fieldId];
  });

  agInit(params): void {
    this.params = params;
    this.updatePerformanceHeaderFilter = params.updatePerformanceHeaderFilter;

    this.displayName.set(params.displayName);
    this.subCategoryId.set(params.subcategoryId);
    this.dataType.set(params.dataType);
    this.fieldId.set(params.fieldId);

    params.column.addEventListener('sortChanged', () => {
      const sort = params.column.getSort();
      this.currentSorting.set(sort);
    });

    params.column.addEventListener('filterChanged', () => {
      this.updateDefaultFilterState();
    });

    this.updateDefaultFilterState();
  }

  private updateDefaultFilterState(): void {
    const filterActive = this.params.column.isFilterActive();
    this.hasDefaultFilter.set(filterActive);
  }

  openPerformanceFilterDialog(e: Event) {
    e.stopPropagation();
    const perfKey = this.fieldId();
    const subCategoryId = this.subCategoryId();

    if (!subCategoryId || !perfKey) {
      return;
    }

    const performanceOption = this.#filtersStore
      .getSubcategoryPerformances(subCategoryId)()
      .find(f => f.performanceKey === perfKey);

    if (!performanceOption) {
      return;
    }

    const formatedLabel = this.#resourcesService.getTextResourceWithPrefixSuffix({
      resourceId: performanceOption.descriptionId,
      prefixResourceId: performanceOption.prefixResourceId,
      suffixResourceId: performanceOption.suffixResourceId,
    });

    const filterValue = this.currentFilters()?.[perfKey];

    this.#dialogService
      .openDialog(OutputFilterDialogComponent, 'md', {
        title: formatedLabel,
        unitOfMeasureSymbol: performanceOption.unitOfMeasurement,
        filterValue,
      } as OutputFilterDialogData)
      .afterClosed()
      .pipe(take(1))
      .subscribe((filterToApply: 'RESET' | OutputFilter | undefined) => {
        if (filterToApply && filterToApply !== 'RESET') {
          this.#filtersStore.setSubcategoryPerformanceFilter(subCategoryId, perfKey, {
            id: performanceOption.id,
            circuit: performanceOption.circuit,
            unitOfMeasurement: performanceOption.unitOfMeasurement,
            resourceId: performanceOption.resourceId,
            prefixResourceId: performanceOption.prefixResourceId,
            suffixResourceId: performanceOption.suffixResourceId,
            ...(filterToApply as OutputFilter),
          } as PerformanceFilter);
          this.updatePerformanceHeaderFilter?.();
        }

        if (filterToApply === 'RESET') {
          this.#filtersStore.removeSubcategoryPerformanceFilter(subCategoryId, perfKey);
          this.updatePerformanceHeaderFilter?.();
        }
      });
  }

  @HostListener('mouseenter')
  onMouseEnter() {
    this.headerHovered.set(true);
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    this.headerHovered.set(false);
  }

  @HostListener('click')
  switchSorting() {
    switch (this.currentSorting()) {
      case 'asc':
        this.params.setSort('desc');
        break;
      case 'desc':
        this.params.setSort(null);
        break;
      default:
        this.params.setSort('asc');
    }
  }

  showDefaultFilterMenu(event: Event): void {
    event.stopPropagation();
    this.params.showColumnMenu(event.target as HTMLElement);
  }
}
